import React, { useEffect, useRef, useState } from 'react';

const LINE_STYLE = {
  entry: { color: '#38bdf8', title: 'ENTRY', lineStyle: 0 },
  sl: { color: '#f43f5e', title: 'SL', lineStyle: 2 },
  tp: { color: '#22c55e', title: 'TP', lineStyle: 2 },
};
const tzShift = -new Date().getTimezoneOffset() * 60;

export const SimChart = ({ candles, digits, levels, onDrag, chartKey }) => {
  const box = useRef(null);
  const seriesRef = useRef(null);
  const chartRef = useRef(null);
  const linesRef = useRef({});
  const valsRef = useRef(levels);
  const dragRef = useRef(null);
  const onDragRef = useRef(onDrag);
  const loadedKey = useRef(null);
  const lastTime = useRef(0);
  const [ready, setReady] = useState(false);
  const [dragging, setDragging] = useState(null);
  valsRef.current = levels;
  onDragRef.current = onDrag;

  useEffect(() => {
    let chart;
    let cancelled = false;
    import('lightweight-charts').then(({ createChart, CandlestickSeries }) => {
      if (cancelled || !box.current) return;
      chart = createChart(box.current, {
        autoSize: true,
        layout: { background: { color: 'transparent' }, textColor: '#8b9bb4', fontFamily: 'IBM Plex Mono, monospace', fontSize: 11 },
        grid: { vertLines: { color: 'rgba(139,155,180,0.07)' }, horzLines: { color: 'rgba(139,155,180,0.07)' } },
        rightPriceScale: { borderColor: 'rgba(139,155,180,0.2)' },
        timeScale: { borderColor: 'rgba(139,155,180,0.2)', timeVisible: true, secondsVisible: false, rightOffset: 6 },
        crosshair: { mode: 0 },
        localization: { locale: 'en-US' },
      });
      seriesRef.current = chart.addSeries(CandlestickSeries, {
        upColor: '#22c55e', downColor: '#ef4444', borderVisible: false,
        wickUpColor: '#22c55e', wickDownColor: '#ef4444',
        autoscaleInfoProvider: (orig) => {
          const r = orig();
          const vals = Object.values(valsRef.current || {}).filter(Boolean);
          if (!r || !vals.length) return r;
          return { ...r, priceRange: {
            minValue: Math.min(r.priceRange.minValue, ...vals),
            maxValue: Math.max(r.priceRange.maxValue, ...vals) } };
        },
      });
      chartRef.current = chart;
      setReady(true);
    });
    return () => { cancelled = true; if (chart) chart.remove(); linesRef.current = {}; };
  }, []);

  useEffect(() => {
    const s = seriesRef.current;
    if (!ready || !s || !candles?.length) return;
    const data = candles.map((c) => ({ ...c, time: c.time + tzShift }));
    if (loadedKey.current !== chartKey) {
      s.applyOptions({ priceFormat: { type: 'price', precision: digits, minMove: 1 / 10 ** digits } });
      s.setData(data);
      chartRef.current.timeScale().scrollToRealTime();
      loadedKey.current = chartKey;
    } else {
      data.filter((c) => c.time >= lastTime.current).forEach((c) => s.update(c));
    }
    lastTime.current = data[data.length - 1].time;
  }, [candles, ready, chartKey, digits]);

  useEffect(() => {
    const s = seriesRef.current;
    if (!ready || !s) return;
    Object.entries(LINE_STYLE).forEach(([k, st]) => {
      const price = levels?.[k];
      const line = linesRef.current[k];
      if (!price) { if (line) { s.removePriceLine(line); delete linesRef.current[k]; } return; }
      if (line) line.applyOptions({ price, lineWidth: dragging === k ? 3 : 2 });
      else linesRef.current[k] = s.createPriceLine({ price, color: st.color, lineWidth: 2, lineStyle: st.lineStyle, axisLabelVisible: true, title: st.title });
    });
  }, [levels, ready, dragging]);

  useEffect(() => {
    const el = box.current;
    const yOf = (e) => { const p = e.touches ? e.touches[0] : e; return p.clientY - el.getBoundingClientRect().top; };
    const hit = (y, tol) => {
      let best = null; let bd = tol;
      Object.keys(LINE_STYLE).forEach((k) => {
        const v = valsRef.current?.[k];
        const c = v && seriesRef.current?.priceToCoordinate(v);
        if (c != null && Math.abs(c - y) < bd) { bd = Math.abs(c - y); best = k; }
      });
      return best;
    };
    const down = (e) => {
      if (!seriesRef.current) return;
      const k = hit(yOf(e), e.touches ? 24 : 10);
      if (!k) return;
      e.stopPropagation();
      if (e.cancelable) e.preventDefault();
      chartRef.current?.priceScale('right').applyOptions({ autoScale: false });
      dragRef.current = k;
      setDragging(k);
    };
    const move = (e) => {
      const k = dragRef.current;
      if (!k) {
        if (!e.touches && el.contains(e.target)) el.style.cursor = hit(yOf(e), 10) ? 'ns-resize' : '';
        return;
      }
      if (e.cancelable) e.preventDefault();
      const p = seriesRef.current.coordinateToPrice(yOf(e));
      if (p != null) onDragRef.current(k, p);
    };
    const up = () => {
      if (!dragRef.current) return;
      dragRef.current = null;
      setDragging(null);
      chartRef.current?.priceScale('right').applyOptions({ autoScale: true });
    };
    el.addEventListener('mousedown', down, true);
    el.addEventListener('touchstart', down, { capture: true, passive: false });
    window.addEventListener('mousemove', move);
    window.addEventListener('touchmove', move, { passive: false });
    window.addEventListener('mouseup', up);
    window.addEventListener('touchend', up);
    return () => {
      el.removeEventListener('mousedown', down, true);
      el.removeEventListener('touchstart', down, { capture: true });
      window.removeEventListener('mousemove', move);
      window.removeEventListener('touchmove', move);
      window.removeEventListener('mouseup', up);
      window.removeEventListener('touchend', up);
    };
  }, []);

  return (
    <div className="relative h-full w-full" data-testid="sim-chart">
      <div ref={box} className="h-full w-full" />
      {dragging && (
        <div className="sim-mono pointer-events-none absolute left-3 top-3 rounded-md bg-black/60 px-2 py-1 text-xs text-white" data-testid="sim-chart-drag-indicator">
          Moving {LINE_STYLE[dragging].title}
        </div>
      )}
    </div>
  );
};
